import React, { useState } from "react";

import ROUTES from "manager/config/routes";
import {
  useResetPropertyVeroGoToPortfolioSettings,
  useTogglePropertyVeroGo,
} from "manager/hooks/api/propertyQueries";

import useModal from "shared/hooks/useModal";

import { GreenSwitch, openVeroGoOverrideDialog, SettingsLink } from "./shared";

type CustomPropertyCardProps = {
  property: any;
  portfolioName: string;
  portfolioActive: boolean;
  isLocked: boolean;
  onAfterReset?: (portfolioId: number) => void;
  onAfterToggle?: (newValue: boolean) => void;
  onStartMutation?: (itemType?: string, itemId?: number) => void;
  flash?: boolean;
};

const RefreshIcon = ({ color = "#7c3aed" }: { color?: string }) => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M20 12C20 16.4183 16.4183 20 12 20C8.58579 20 5.67157 17.8593 4.52591 14.8497"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M4 12C4 7.58172 7.58172 4 12 4C15.4142 4 18.3284 6.14066 19.4741 9.15029"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M16.5 8.5H19.5V5.5"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.5 15.5H4.5V18.5"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CustomPropertyCard = ({
  property,
  portfolioName,
  portfolioActive,
  isLocked,
  onAfterReset,
  onAfterToggle,
  onStartMutation,
  flash,
}: CustomPropertyCardProps) => {
  const [isResetting, setIsResetting] = useState(false);
  const { openModalDialog } = useModal();
  const { togglePropertyVeroGo, isTogglePropertyVeroGoLoading } =
    useTogglePropertyVeroGo({ propertyId: property.id });
  const { resetPropertyVeroGoToPortfolioSettings } =
    useResetPropertyVeroGoToPortfolioSettings();

  const isActive = !!property.veroGoActive;
  const hasMixedDealSetupInheritance =
    Boolean(property.isDealSetupFromPortfolio) !==
    Boolean(property.isPrescreeningDealSetupFromPortfolio);
  const isDisabled = isTogglePropertyVeroGoLoading || isResetting || isLocked;
  const portfolioStateLabel = portfolioActive ? "On" : "Off";

  const handleReset = async () => {
    const hasVeroGoMismatch = isActive !== portfolioActive;

    if (hasVeroGoMismatch) {
      const result = await openVeroGoOverrideDialog(openModalDialog, {
        title: "Reset to portfolio setting?",
        subtitle: `${property.name} in ${portfolioName}`,
        nowState: isActive ? "on" : "off",
        nowRelationship: "custom",
        afterState: portfolioActive ? "on" : "off",
        afterRelationship: "inherited",
        infoText: (
          <>
            This will <strong>re-inherit</strong> settings from {portfolioName}.
            Both screening and pre-screening settings will be updated to match
            the portfolio.
          </>
        ),
        primaryButtonLabel: `Reset to ${portfolioStateLabel}`,
        secondaryButtonLabel: "Keep custom",
        primaryValue: "confirm",
        secondaryValue: "cancel",
      });

      if (result !== "confirm") return;
    }

    try {
      onStartMutation?.("property", property.id);
      setIsResetting(true);
      await resetPropertyVeroGoToPortfolioSettings(property.id);
    } finally {
      setIsResetting(false);
      await onAfterReset?.(property.portfolio);
    }
  };

  const handleToggle = async (checked: boolean) => {
    if (checked && hasMixedDealSetupInheritance) {
      const result = await openVeroGoOverrideDialog(openModalDialog, {
        title: "Enable Vero Go for this property?",
        subtitle: `${property.name} in ${portfolioName}`,
        nowState: isActive ? "on" : "off",
        nowRelationship: "custom",
        afterState: "on",
        afterRelationship: "custom",
        infoText: (
          <>
            This property currently has mixed inheritance between screening and
            pre-screening settings. Enabling Vero Go will preserve existing
            custom settings and create a property copy for the inherited side so
            both remain managed together for this property.
          </>
        ),
        primaryButtonLabel: "Enable Vero Go",
        secondaryButtonLabel: "Cancel",
        primaryValue: "confirm",
        secondaryValue: "cancel",
      });

      if (result !== "confirm") return;
    }

    onStartMutation?.("property", property.id);
    togglePropertyVeroGo(checked, {
      onSettled: () => onAfterToggle?.(checked),
    });
  };

  return (
    <div
      data-vero-go-item={`property-${property.id}`}
      style={{
        background: "#fff",
        border: "1px solid #c4b5fd",
        borderRadius: 12,
        borderLeft: "3px solid #7c3aed",
        padding: "20px",
        marginBottom: 14,
      }}
      className={flash ? "vero-go-flash" : undefined}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <div style={{ flex: 1 }}>
          <SettingsLink
            to={ROUTES.property.replace(":id", String(property.id))}
            style={{ fontWeight: 600, fontSize: 14 }}
          >
            {property.name}
          </SettingsLink>
          {property.address && (
            <div style={{ color: "#8C8C8C", fontSize: 12, marginTop: 4 }}>
              {property.address}
              {property.city ? `, ${property.city}` : ""}
              {property.state ? `, ${property.state}` : ""}
            </div>
          )}
          <div
            style={{
              color: "#6B7280",
              fontSize: 12,
              marginTop: 6,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span>
              Portfolio:{" "}
              <span style={{ fontWeight: 400 }}>{portfolioName}</span>
            </span>
            <span style={{ color: "#9CA3AF" }}>|</span>
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  background: portfolioActive ? "#059669" : "#9CA3AF",
                  display: "inline-block",
                }}
              />
              Status:{" "}
              <span style={{ fontWeight: 400 }}>{portfolioStateLabel}</span>
            </span>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={handleReset}
            disabled={isResetting || isLocked}
            style={{
              background: "#f5f3ff",
              border: "1px solid #c4b5fd",
              color: isResetting || isLocked ? "#ccc" : "#7c3aed",
              cursor: isResetting || isLocked ? "default" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              fontSize: 12,
              fontWeight: 600,
              padding: "8px 10px",
              borderRadius: 10,
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: 6,
                flexShrink: 0,
              }}
            >
              <RefreshIcon color="#7c3aed" />
            </span>
            Reset to Portfolio
          </button>
          <GreenSwitch
            checked={isActive}
            loading={isTogglePropertyVeroGoLoading || isResetting}
            onChange={handleToggle}
            disabled={isDisabled}
          />
        </div>
      </div>
      <div
        style={{
          marginTop: 16,
          background: "#F3F4F6",
          borderRadius: 10,
          padding: "14px 16px",
          color: "#7c3aed",
          fontSize: 12,
          lineHeight: 1.45,
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            marginTop: 1,
          }}
        >
          <RefreshIcon color="#7c3aed" />
        </span>
        <span>
          This property is decoupled from portfolio settings. Reset to
          re-inherit the portfolio&apos;s current setting (
          <strong>{portfolioActive ? "On" : "Off"}</strong>).
        </span>
      </div>
    </div>
  );
};

export default CustomPropertyCard;
